import { Component, Inject, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  AvailabilityDay,
  AvailabilityWindowInput,
  InMemoryDataService,
  InstructorAvailabilityScheduleInput
} from '../../core/data/in-memory-data.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface InstructorAvailabilityDialogData {
  instructorId: string | number;
  instructorName: string;
}

const DAYS: AvailabilityDay[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

@Component({
  selector: 'app-instructor-availability-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatInputModule, MatProgressBarModule, TranslatePipe, EnterSubmitDirective],
  template: `
    <h2 mat-dialog-title>{{ 'availability.title' | t }}</h2>
    @if (loading()) { <mat-progress-bar mode="indeterminate" /> }
    <form appEnterSubmit (ngSubmit)="save()">
    <mat-dialog-content>
      <p class="subtitle">{{ data.instructorName }} · Europe/Warsaw</p>
      @if (!loading()) {
        @if (!configured() && !editing()) {
          <div class="unrestricted">
            <mat-icon>event_available</mat-icon>
            <div><strong>{{ 'availability.unrestricted' | t }}</strong><p>{{ 'availability.unrestrictedHint' | t }}</p></div>
          </div>
          <button mat-flat-button type="button" color="primary" (click)="editing.set(true)">{{ 'availability.configure' | t }}</button>
        } @else {
          <div class="validity">
            <label>{{ 'availability.validFrom' | t }}<input type="date" [(ngModel)]="validFrom" [ngModelOptions]="{standalone: true}" /></label>
            <label>{{ 'availability.validTo' | t }}<input type="date" [(ngModel)]="validTo" [ngModelOptions]="{standalone: true}" /></label>
          </div>
          @if (dateError()) { <p class="error">{{ 'availability.invalidDates' | t }}</p> }
          <div class="schedule-heading">
            <div><strong>{{ 'availability.weeklySchedule' | t }}</strong><span>{{ 'availability.weeklyScheduleHint' | t }}</span></div>
          </div>
          <div class="week">
            @for (day of days; track day) {
              <section class="day">
                <div class="day-name"><strong>{{ ('availability.day.' + day) | t }}</strong></div>
                <div class="windows">
                  @for (window of windows()[day]; track $index; let index = $index) {
                    <div class="window-row">
                      <input type="time" [ngModel]="window.startsAtTime" [ngModelOptions]="{standalone: true}" (ngModelChange)="updateWindow(day, index, 'startsAtTime', $event)" />
                      <span>–</span>
                      <input type="time" [ngModel]="window.endsAtTime" [ngModelOptions]="{standalone: true}" (ngModelChange)="updateWindow(day, index, 'endsAtTime', $event)" />
                      <button mat-icon-button type="button" (click)="removeWindow(day, index)" [attr.aria-label]="'common.delete' | t"><mat-icon>delete_outline</mat-icon></button>
                    </div>
                  } @empty { <span class="unavailable">{{ 'availability.unavailable' | t }}</span> }
                  <button mat-button type="button" class="add-window" (click)="addWindow(day)"><mat-icon>add</mat-icon>{{ 'availability.addWindow' | t }}</button>
                  @if (dayError(day)) { <p class="error">{{ dayError(day) | t }}</p> }
                </div>
              </section>
            }
          </div>
        }
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(false)">{{ 'common.cancel' | t }}</button>
      @if (!loading() && (configured() || editing())) {
        <button mat-flat-button type="submit" color="primary" [disabled]="saving() || invalid()">{{ 'availability.save' | t }}</button>
      }
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    :host { color:#172033; } h2 { font-size:1.45rem; font-weight:800; }
    mat-dialog-content { padding-top:4px; background:#f8fafc; }
    mat-dialog-actions { position:sticky; bottom:0; z-index:2; min-height:64px; padding:10px 24px; border-top:1px solid #dce4ef; background:#fff; }
    .subtitle { margin:0 0 20px; color:#52657d; font-size:.95rem; }
    .unrestricted { display:flex; gap:14px; padding:18px; margin-bottom:18px; border:1px solid #a9ddcb; border-radius:10px; background:#eef9f5; color:#087858; }
    .unrestricted mat-icon { width:30px; height:30px; font-size:30px; } .unrestricted p { margin:5px 0 0; color:#52657d; line-height:1.45; }
    .validity { display:grid; grid-template-columns:1fr 1fr; gap:18px; padding:18px; margin-bottom:20px; border:1px solid #d7e1f0; border-radius:10px; background:#fff; }
    label { display:grid; gap:8px; color:#31445f; font-size:.85rem; font-weight:800; }
    input { min-height:44px; box-sizing:border-box; padding:8px 11px; border:1px solid #b9c9df; border-radius:7px; background:#fff; color:#172033; font-size:.95rem; }
    input:focus { border-color:#009b72; outline:2px solid rgba(0,155,114,.14); }
    .schedule-heading { display:flex; align-items:end; justify-content:space-between; margin:4px 0 12px; }
    .schedule-heading div { display:grid; gap:4px; } .schedule-heading strong { font-size:1.05rem; } .schedule-heading span { color:#64748b; font-size:.86rem; }
    .week { min-width:700px; display:grid; gap:10px; padding-bottom:4px; }
    .day { display:grid; grid-template-columns:155px 1fr; gap:20px; padding:16px 18px; border:1px solid #d7e1f0; border-left:4px solid #009b72; border-radius:9px; background:#fff; }
    .day-name { padding-top:11px; font-size:.95rem; } .windows { display:grid; gap:10px; }
    .window-row { display:flex; align-items:center; gap:10px; } .window-row input { width:135px; font-weight:700; }
    .window-row > span { color:#64748b; font-weight:700; } .window-row button { color:#52657d; }
    .add-window { justify-self:start; min-height:36px; color:#007e5e; font-weight:700; }
    .unavailable { padding:12px 0 3px; color:#7a889b; font-style:italic; }
    .error { margin:2px 0; color:#b42318; font-size:.84rem; font-weight:600; }
    @media (max-width:720px) { mat-dialog-content { padding-inline:16px; } .week { min-width:0; } .validity, .day { grid-template-columns:1fr; } .day { gap:8px; padding:14px; } .day-name { padding-top:0; } .window-row { gap:6px; } .window-row input { min-width:0; width:112px; } }
  `
})
export class InstructorAvailabilityDialogComponent implements OnInit {
  private readonly dataService = inject(InMemoryDataService);
  readonly days = DAYS;
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly configured = signal(false);
  readonly editing = signal(false);
  readonly windows = signal<Record<AvailabilityDay, AvailabilityWindowInput[]>>(this.emptyWindows());
  validFrom = '';
  validTo = '';

  dateError(): boolean { return !!this.validFrom && !!this.validTo && this.validTo < this.validFrom; }
  invalid(): boolean { return this.dateError() || DAYS.some((day) => !!this.dayError(day)); }

  constructor(
    readonly dialogRef: MatDialogRef<InstructorAvailabilityDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) readonly data: InstructorAvailabilityDialogData
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const schedule = await this.dataService.getInstructorAvailabilitySchedule(this.data.instructorId);
      this.configured.set(schedule.configured);
      this.editing.set(schedule.configured);
      this.validFrom = schedule.validFrom ?? '';
      this.validTo = schedule.validTo ?? '';
      this.windows.set(Object.fromEntries(DAYS.map((day) => [day, (schedule.windows[day] ?? []).map(({ startsAtTime, endsAtTime }) => ({ startsAtTime, endsAtTime }))])) as Record<AvailabilityDay, AvailabilityWindowInput[]>);
    } finally { this.loading.set(false); }
  }

  addWindow(day: AvailabilityDay): void {
    this.windows.update((all) => {
      const latestEnd = [...all[day]].map((item) => item.endsAtTime).sort().at(-1);
      const startsAtTime = latestEnd && latestEnd < '23:00' ? latestEnd : '08:00';
      const endHour = Math.min(23, Number(startsAtTime.slice(0, 2)) + 1);
      const endsAtTime = `${`${endHour}`.padStart(2, '0')}:${startsAtTime.slice(3, 5)}`;
      return { ...all, [day]: [...all[day], { startsAtTime, endsAtTime }] };
    });
  }
  removeWindow(day: AvailabilityDay, index: number): void { this.windows.update((all) => ({ ...all, [day]: all[day].filter((_, i) => i !== index) })); }
  updateWindow(day: AvailabilityDay, index: number, key: keyof AvailabilityWindowInput, value: string): void {
    this.windows.update((all) => ({ ...all, [day]: all[day].map((item, i) => i === index ? { ...item, [key]: value } : item) }));
  }
  dayError(day: AvailabilityDay): string {
    const sorted = [...this.windows()[day]].sort((a, b) => a.startsAtTime.localeCompare(b.startsAtTime));
    if (sorted.some((item) => !item.startsAtTime || !item.endsAtTime || item.endsAtTime <= item.startsAtTime)) return 'availability.invalidWindow';
    if (sorted.some((item, index) => index > 0 && item.startsAtTime < sorted[index - 1].endsAtTime)) return 'availability.overlap';
    return '';
  }
  async save(): Promise<void> {
    if (this.invalid()) return;
    this.saving.set(true);
    const payload: InstructorAvailabilityScheduleInput = { timezone: 'Europe/Warsaw', validFrom: this.validFrom || null, validTo: this.validTo || null, windows: this.windows() };
    try { await this.dataService.putInstructorAvailabilitySchedule(this.data.instructorId, payload); this.dialogRef.close(true); }
    finally { this.saving.set(false); }
  }
  private emptyWindows(): Record<AvailabilityDay, AvailabilityWindowInput[]> {
    return Object.fromEntries(DAYS.map((day) => [day, [] as AvailabilityWindowInput[]])) as unknown as Record<AvailabilityDay, AvailabilityWindowInput[]>;
  }
}
